import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Dialog,
  FormControlLabel,
  FormGroup,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { LoadingButton } from '@mui/lab';
import CancelIcon from '@mui/icons-material/Cancel';
import QueueIcon from '@mui/icons-material/Queue';
import GroupsIcon from '@mui/icons-material/Groups';
import PersonIcon from '@mui/icons-material/Person';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import dayjs from 'dayjs';
import Landing from '../../../../Models/LandingModels/Landing';
import WeightNote from '../../../../Models/WeightNoteModels/WeightNote';
import Email from '../../../../Models/EmailModels/Email';
import EmailGroup from '../../../../Models/EmailModels/EmailGroup';
import EmailModal from '../../../../SharedComponents/Header/components/EmailModal/EmailModal';
import { getEmailGroups, getEmails, sendEmailWeightNote } from '../../../../Services/LondunarkerfiAPIService';
import EmailSend from '../../../../Models/EmailModels/EmailSend';
import User from '../../../../Models/UserModels/User';
import { MobilePaperComponent, PaperComponent } from '../../../../SharedComponents/Paper/CustomPaper';
import { logError } from '../../../../Helpers/LogError';

interface SendWeightNoteModalProps {
  user: User;
  open: boolean;
  toggleOpen: () => void;
  showSnackbar: (message: string, severity: string) => void;
  landing: Landing;
  weightNote: WeightNote;
  isMobileView: boolean;
}

type RecipientOption = {
  type: 'email' | 'group';
  value: string;
  label: string;
  subLabel?: string;
  emails?: string[];
  icon?: 'person' | 'group' | 'ship';
};

const SendWeightNoteModal: FC<SendWeightNoteModalProps> = (props: SendWeightNoteModalProps) => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [emailGroups, setEmailGroups] = useState<EmailGroup[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  const [selectedEmailGroups, setSelectedEmailGroups] = useState<string[]>([]);
  const [subject, setSubject] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [sendReceiptToSender, setSendReceiptToSender] = useState<boolean>(props.user.settings.sendCopyToSender);
  const [loading, setLoading] = useState<boolean>(false);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [openEmailControl, setOpenEmailControl] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (props.user && props.open) {
          const fetchedEmails = await getEmails(props.user?.userPickedHarbour);
          setEmails(fetchedEmails.sort((a, b) => a?.email.localeCompare(b?.email)));

          const fetchedEmailGroups = await getEmailGroups(props.user?.userPickedHarbour);
          const filteredEmailGroups = fetchedEmailGroups.filter(group => group.emails.length > 0);
          const sortedEmailGroup = filteredEmailGroups.sort((a, b) => a?.description.localeCompare(b?.description));
          setEmailGroups(sortedEmailGroup);

          const contactGroup = fetchedEmailGroups.find(groupWithContact => groupWithContact?.contact?.id === props.weightNote?.receiver?.id);
          const shipGroup = fetchedEmailGroups.find(groupWithShip => groupWithShip?.ship?.shipRegistrationNumber === props.landing?.ship?.shipRegistrationNumber);

          const preselectedGroups: string[] = [];
          if (contactGroup) {
            preselectedGroups.push(contactGroup.description);
          }
          if (shipGroup) {
            preselectedGroups.push(shipGroup.description);
          }
          setSelectedEmailGroups(preselectedGroups);
          setSendReceiptToSender(props.user.settings.sendCopyToSender);
        }
      } catch (error) {
        logError(error);
      }
    };

    fetchData();

  }, [props.open, props.user, props.weightNote?.receiver?.id, openEmailControl, props.landing?.ship?.shipRegistrationNumber]);

  useEffect(() => {
    setSubject(
      (props.weightNote.weightNoteDate ? dayjs(props.weightNote.weightNoteDate).format('DD.MM.YYYY') : '') +
      ' - ' +
      props.landing.ship?.name +
      ' - ' +
      props.landing.harbour?.harbourName +
      ' - ' +
      (props.weightNote?.landingId ? 'Löndun ' + props.weightNote.landingId : '') +
      ' - ' +
      (props.weightNote?.noteNumber ? 'Vigtarnóta ' + props.weightNote.noteNumber : '')
    );

    setContent(
      (props.weightNote.receiver?.name ? props.weightNote.receiver?.name + ' - ' : '') +
      (props.weightNote.receiver?.ssn ? props.weightNote.receiver?.ssn : '')
    );
  }, [props.weightNote, props.landing]);

  const emailOptions = useMemo<RecipientOption[]>(() => emails.map(email => ({
    type: 'email' as const,
    value: email.email,
    label: email.email,
    subLabel: email.comment,
    icon: 'person' as const,
  })), [emails]);

  const groupOptions = useMemo<RecipientOption[]>(() => emailGroups.map(group => ({
    type: 'group' as const,
    value: group.description,
    label: group.description,
    subLabel: group.contact?.name ?? group.ship?.name,
    emails: group.emails.map(e => e.email),
    icon: group.ship ? 'ship' as const : 'group' as const,
  })), [emailGroups]);

  const selectedEmailOptions = useMemo<RecipientOption[]>(() =>
    emailOptions.filter(option => selectedEmails.includes(option.value)),
  [emailOptions, selectedEmails]);

  const selectedGroupOptions = useMemo<RecipientOption[]>(() =>
    groupOptions.filter(option => selectedEmailGroups.includes(option.value)),
  [groupOptions, selectedEmailGroups]);

  const emailFilterOptions = createFilterOptions<RecipientOption>({
    stringify: (option) => `${option.label} ${option.subLabel ?? ''} ${(option.emails ?? []).join(' ')}`,
  });

  const groupFilterOptions = createFilterOptions<RecipientOption>({
    stringify: (option) => `${option.label} ${option.subLabel ?? ''} ${(option.emails ?? []).join(' ')}`,
  });

  const handleEmailsChange = (_: unknown, values: RecipientOption[]) => {
    setSelectedEmails(values.map(v => v.value));
  };

  const handleGroupsChange = (_: unknown, values: RecipientOption[]) => {
    setSelectedEmailGroups(values.map(v => v.value));
  };

  const handleClose = () => {
    setSelectedEmailGroups([]);
    setSelectedEmails([]);
    props.toggleOpen();
  };

  const handleSubjectChange = (nextSubject: string) => {
    setSubject(nextSubject);
  };

  const handleContentChange = (nextContent: string) => {
    setContent(nextContent);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const selectedEmailObjects: Email[] = [];

      selectedEmailGroups.forEach(groupDescription => {
        const group = emailGroups.find(g => g.description === groupDescription);
        if (group) {
          selectedEmailObjects.push(...group.emails);
        }
      });

      selectedEmails.forEach(email => {
        if (!selectedEmailObjects.find(e => e.email === email)) {
          selectedEmailObjects.push({ email: email });
        }
      });

      const uniqueEmailAddresses = Array.from(new Set(selectedEmailObjects.map(email => email.email)));
      if (sendReceiptToSender) {
        uniqueEmailAddresses.push(props.user.email);
      }

      const emailBody: EmailSend = {
        weightNoteId: props.weightNote.id,
        emailAddress: uniqueEmailAddresses,
        replyTo: props.user.email,
        subject: subject,
        body: content
      };
      await sendEmailWeightNote(emailBody);
      props.showSnackbar('Vigtarnóta send', 'success');

      props.weightNote.emailSent = true;

      handleClose();
      setLoading(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setLoading(false);
      if (error.response && error.response.data && typeof error.response.data !== 'object') {
        const errorMsg = error.response.data;
        props.showSnackbar(errorMsg, 'error');
      }
      else {
        logError(error);
        props.showSnackbar('Óvænt villa kom upp', 'error');
      }
    }
  };

  const toggleEmailControlModal = () => {
    setOpenEmailControl(!openEmailControl);
  };

  const toggleSendReceiptToSender = () => {
    setSendReceiptToSender(!sendReceiptToSender);
  };

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      PaperComponent={props.isMobileView ? MobilePaperComponent : PaperComponent}
      aria-labelledby="draggable-dialog-title"
      maxWidth="sm"
    >
      <EmailModal
        open={openEmailControl}
        toggleOpen={toggleEmailControlModal}
        showSnackbar={props.showSnackbar}
        user={props.user}
      />
      <div id="draggable-dialog-title"
        style={{ width: '100%' }}
      >
        <Typography style={{ fontWeight: 'bold', lineHeight: '1.5em', fontSize: '1.5em', cursor: 'move' }}>
          Senda vigtarnótu
        </Typography>
      </div>
      <div style={{ position: 'absolute', top: 0, right: 0 }}>
        <Tooltip title="Stjórna netföngum">
          <IconButton color='primary' size='large' onClick={toggleEmailControlModal}>
            <QueueIcon />
          </IconButton>
        </Tooltip>
        <IconButton onClick={handleClose}>
          <CancelIcon fontSize='large' />
        </IconButton>
      </div>
      <div>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={emailOptions}
          value={selectedEmailOptions}
          getOptionLabel={(option) => option.label}
          filterOptions={emailFilterOptions}
          onChange={handleEmailsChange}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={`${option.type}-${option.value}`} style={{ paddingLeft: 8, paddingRight: 8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <PersonIcon fontSize="small" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={selected ? 'bold' : 'regular'} noWrap>
                    {option.label}
                  </Typography>
                  {option.subLabel && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.subLabel}
                    </Typography>
                  )}
                  {option.type === 'group' && option.emails && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.emails.join(', ')}
                    </Typography>
                  )}
                </Box>
                <Checkbox checked={selected} />
              </Box>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={`${option.type}-${option.value}`}
                icon={<PersonIcon />}
                label={option.label}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Veldu viðtakendur"
              placeholder="Leita að netfangi"
            />
          )}
          disabled={loading}
          fullWidth
        />
      </div>
      <div style={{ marginTop: '1em' }}>
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={groupOptions}
          value={selectedGroupOptions}
          getOptionLabel={(option) => option.label}
          filterOptions={groupFilterOptions}
          onChange={handleGroupsChange}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={`${option.type}-${option.value}`} style={{ paddingLeft: 8, paddingRight: 8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                {option.icon === 'ship' ? <DirectionsBoatIcon fontSize="small" /> : <GroupsIcon fontSize="small" />}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={selected ? 'bold' : 'regular'} noWrap>
                    {option.label}
                  </Typography>
                  {option.subLabel && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.subLabel}
                    </Typography>
                  )}
                  {option.emails && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {option.emails.join(', ')}
                    </Typography>
                  )}
                </Box>
                <Checkbox checked={selected} />
              </Box>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={`${option.type}-${option.value}`}
                icon={option.icon === 'ship' ? <DirectionsBoatIcon /> : <GroupsIcon />}
                label={option.label}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Veldu hópa"
              placeholder="Leita að hóp eða skipi"
            />
          )}
          disabled={loading}
          fullWidth
        />
      </div>

      <TextField
        label="Efni"
        value={subject}
        onChange={(event) => handleSubjectChange(event.target.value)}
      />
      <TextField
        label="Meginmál"
        multiline
        rows={4}
        value={content}
        onChange={(event) => handleContentChange(event.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.stopPropagation();
          }
        }}
      />
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={sendReceiptToSender}
              onChange={toggleSendReceiptToSender}
            />
          }
          label="Senda afrit á sendanda"
        />
      </FormGroup>
      <LoadingButton
        ref={submitButtonRef}
        size="small"
        onClick={handleSubmit}
        loading={loading}
        disabled={loading || (selectedEmails.length === 0 && selectedEmailGroups.length === 0) || subject === '' || content === ''}
        variant="contained"
      >
        <span>Senda vigtarnótu</span>
      </LoadingButton>
    </Dialog >
  );
};

export default SendWeightNoteModal;
